/**
 * HERO — trust / compliance row (prototype "VERTRAUEN / COMPLIANCE", desktop
 * lines 2043–2074). Sits between the product window and the scroll stage:
 * DSGVO · Daten in der EU · Entwickelt in Deutschland · Revisionssicher · GoBD.
 */
import { ld } from "@/lib/theme/light-dark";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

import {
  AuditProofIcon,
  EuropeMapIcon,
  GermanyIcon,
  RetentionIcon,
} from "./hero-compliance-icons";
import { H } from "./hero-tokens";

/** 12 EU stars evenly on a circle (r 6.6 of 11), upright like the official flag. */
const EU_STARS: readonly (readonly [number, number])[] = Array.from({ length: 12 }, (_, i) => {
  const a = -Math.PI / 2 + (i * Math.PI) / 6;
  return [11 + 6.6 * Math.cos(a), 11 + 6.6 * Math.sin(a)] as const;
});
/** Five-pointed star, outer radius 1. */
const STAR_PATH =
  "M0,-1 0.225,-0.309 0.951,-0.309 0.363,0.118 0.588,0.809 0,0.382 -0.588,0.809 -0.363,0.118 -0.951,-0.309 -0.225,-0.309Z";

export function EuFlag({ size = 20 }: { readonly size?: number }): ReactNode {
  const t = useTranslations("marketing.heroCompliance");
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        display: "inline-flex",
        flexShrink: 0,
        boxShadow: `0 0 0 1px ${ld("rgba(11,15,25,0.08)", "rgba(250,250,250,0.08)")}`,
      }}
      role="img"
      aria-label={t("euFlaggeServerstandortEu")}
      title={t("serverstandortEu")}
    >
      <svg width={size} height={size} viewBox="0 0 22 22" aria-hidden="true">
        <circle cx="11" cy="11" r="11" fill="#003399" />
        {EU_STARS.map(([cx, cy]) => (
          <path
            key={`${cx}-${cy}`}
            d={STAR_PATH}
            transform={`translate(${cx.toFixed(2)} ${cy.toFixed(2)}) scale(1.25)`}
            fill="#FFCC00"
          />
        ))}
      </svg>
    </span>
  );
}

export function DeFlag({ size = 20 }: { readonly size?: number }): ReactNode {
  const t = useTranslations("marketing.heroCompliance");
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size / 4),
        overflow: "hidden",
        display: "inline-flex",
        flexDirection: "column",
        flexShrink: 0,
        boxShadow: `0 0 0 1px ${ld("rgba(11,15,25,0.08)", "rgba(250,250,250,0.08)")}`,
      }}
      role="img"
      aria-label={t("deutschlandFlaggeEntwickeltDeutschland")}
      title={t("entwickeltDeutschland")}
    >
      <span style={{ flex: 1, background: "#0B0F19" }} />
      <span style={{ flex: 1, background: "#DD0000" }} />
      <span style={{ flex: 1, background: "#FFCE00" }} />
    </span>
  );
}

type ComplianceItem = { readonly icon: (size: number) => ReactNode; readonly labelKey: string };

const ITEMS: readonly ComplianceItem[] = [
  { icon: (s) => <EuFlag size={s} />, labelKey: "dsgvoKonform" },
  { icon: (s) => <EuropeMapIcon size={s} />, labelKey: "alleDatenEu" },
  { icon: (s) => <GermanyIcon size={s} />, labelKey: "entwickeltDeutschland" },
  { icon: (s) => <AuditProofIcon size={s} />, labelKey: "revisionssichereAblage" },
  { icon: (s) => <RetentionIcon size={s} />, labelKey: "gobdKonformesArbeitenMoeglich" },
];

/** Five seals in a row: mark on top, label below — big enough to be read at a glance. */
export function HeroCompliance({
  compact = false,
}: {
  readonly compact?: boolean;
}): ReactNode {
  const t = useTranslations("marketing.heroCompliance");
  const iconSize = compact ? 34 : 42;
  return (
    <ul
      style={{
        // Desktop: clear the overlays hanging below the window.
        margin: compact ? "34px auto 0" : "80px auto 0",
        padding: "0 20px",
        listStyle: "none",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        rowGap: compact ? 22 : 0,
        maxWidth: 1180,
      }}
    >
      {ITEMS.map((item) => (
        <li
          key={item.labelKey}
          style={{
            width: compact ? "33.33%" : 228,
            boxSizing: "border-box",
            padding: compact ? "0 4px" : "0 10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: compact ? 9 : 12,
            textAlign: "center",
          }}
        >
          <span style={{ height: iconSize, display: "inline-flex", alignItems: "center" }}>
            {item.icon(iconSize)}
          </span>
          <span
            style={{
              fontSize: compact ? 12.5 : 15.5,
              fontWeight: 600,
              lineHeight: 1.3,
              color: ld("rgba(11,15,25,0.82)", "rgba(250,250,250,0.85)"),
              fontFamily: H.sans,
            }}
          >
            {t(item.labelKey)}
          </span>
        </li>
      ))}
    </ul>
  );
}
